import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { useNavigate } from "react-router-dom";
import { productUrl } from "@/lib/productUrl";
import { toast } from "sonner";
import { Check, Plus, ShoppingBag, Sparkles, Loader2 } from "lucide-react";

/**
 * BundleBuilder — "Complete the Look"
 * Shows the anchor product (jacket/pants/etc.) + a set of matching-color
 * complementary pieces & shoes returned by the backend. Each piece can be
 * toggled on/off; "Add Bundle to Bag" adds every selected item at once.
 */
export function BundleBuilder({ product, bundle }) {
  const { user } = useAuth();
  const { addItem } = useCart();
  const navigate = useNavigate();

  // by default all bundle items are pre-selected
  const [selected, setSelected] = useState(() => new Set(bundle.map((b) => b.id)));
  const [busy, setBusy] = useState(false);

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const items = useMemo(() => [product, ...bundle], [product, bundle]);
  const chosen = useMemo(
    () => bundle.filter((b) => selected.has(b.id)),
    [bundle, selected]
  );

  const bundleTotal = useMemo(
    () => product.price + chosen.reduce((s, b) => s + (b.price || 0), 0),
    [product.price, chosen]
  );
  const listTotal = useMemo(
    () => product.price + bundle.reduce((s, b) => s + (b.price || 0), 0),
    [product.price, bundle]
  );
  const savings = 0; // no discount for now; leave hook in place if we want to add it later

  const addBundle = async () => {
    if (!user) {
      navigate("/auth");
      return;
    }
    setBusy(true);
    try {
      const addOne = async (p) => {
        const size = (p.sizes || [])[0] || "";
        await addItem({ product_id: p.id, size, quantity: 1, color: p.color || null });
      };
      await addOne(product);
      for (const p of chosen) await addOne(p);
      toast.success(`${chosen.length + 1} pieces added to your bag`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative bg-cream/60 border border-cream rounded-sm overflow-hidden">
      <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-rosemetal/10 blur-3xl pointer-events-none" />
      <div className="relative p-8 md:p-12">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <p className="font-label text-[10px] tracking-[0.3em] text-rosemetal mb-2 flex items-center gap-2">
              <Sparkles className="w-3.5 h-3.5" strokeWidth={1.5} /> COMPLETE THE LOOK
            </p>
            <h2 className="font-display text-3xl md:text-4xl text-ink leading-tight">
              Style the full <em className="italic text-rosemetal">{product.color?.toLowerCase()}</em> ensemble
            </h2>
            <p className="font-body text-sm text-ink/60 mt-2 max-w-lg">
              Hand-matched by our stylists to go with the {product.name}. Tap any piece to add or remove.
            </p>
          </div>
          <div className="text-right">
            <div className="font-label text-[10px] tracking-widest text-ink/50">BUNDLE TOTAL</div>
            <div className="font-display text-4xl text-ink tabular-nums">${bundleTotal.toFixed(0)}</div>
            {savings > 0 && (
              <div className="font-label text-[10px] text-rosemetal">Save ${savings.toFixed(0)}</div>
            )}
          </div>
        </div>

        <div className="flex flex-wrap items-stretch gap-4 md:gap-5">
          {items.map((p, i) => {
            const isAnchor = i === 0;
            const isSelected = isAnchor || selected.has(p.id);
            const img = p.variants?.[0]?.image || p.images?.[0];
            return (
              <div key={p.id} className="flex items-center gap-2 md:gap-3">
                <motion.div
                  whileHover={!isAnchor ? { y: -4 } : {}}
                  className={`relative w-[150px] md:w-[190px] shrink-0 group ${
                    isAnchor ? "" : "cursor-pointer"
                  }`}
                  onClick={() => !isAnchor && toggle(p.id)}
                  data-testid={`bundle-item-${i}`}
                >
                  <div className={`aspect-[3/4] bg-ivory rounded-sm overflow-hidden border transition-all ${
                    isSelected ? "border-rosemetal shadow-lg" : "border-cream opacity-60 grayscale"
                  }`}>
                    {img ? (
                      <img src={img} alt={p.name} className="w-full h-full object-cover object-top" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center font-label text-[10px] text-ink/40">
                        {p.product_type || p.category}
                      </div>
                    )}
                  </div>

                  {/* selection state indicator */}
                  {!isAnchor && (
                    <div className={`absolute top-2 right-2 w-7 h-7 rounded-full flex items-center justify-center transition-all ${
                      isSelected ? "bg-rosemetal text-cream" : "bg-ivory border border-cream text-ink/50"
                    }`}>
                      {isSelected ? <Check className="w-3.5 h-3.5" strokeWidth={2.5} /> : <Plus className="w-3.5 h-3.5" strokeWidth={2} />}
                    </div>
                  )}
                  {isAnchor && (
                    <div className="absolute top-2 left-2 bg-ink text-cream font-label text-[8px] tracking-widest px-2 py-1 rounded-sm">
                      THIS PIECE
                    </div>
                  )}

                  <div className="mt-2">
                    <Link
                      to={productUrl(p)}
                      onClick={(e) => e.stopPropagation()}
                      className="font-body text-[13px] text-ink hover:text-rosemetal transition-colors line-clamp-2 leading-tight"
                    >
                      {p.name}
                    </Link>
                    <div className="flex items-center justify-between mt-1">
                      <span className="font-label text-[9px] text-ink/50 tracking-widest">
                        {p.product_type || p.category}
                      </span>
                      <span className="font-display text-base text-ink">${p.price}</span>
                    </div>
                  </div>
                </motion.div>

                {/* plus separator */}
                {i < items.length - 1 && (
                  <span className="font-display text-3xl text-rosemetal/60 hidden md:block" aria-hidden>
                    +
                  </span>
                )}
              </div>
            );
          })}
        </div>

        <div className="mt-10 flex flex-wrap items-center gap-4">
          <button
            data-testid="add-bundle-btn"
            onClick={addBundle}
            disabled={busy}
            className="bg-ink text-cream px-8 py-4 font-label text-[11px] tracking-[0.25em] rounded-sm hover:bg-rosemetal transition-colors flex items-center gap-2 disabled:opacity-50"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShoppingBag className="w-4 h-4" />}
            ADD {chosen.length + 1} PIECES · ${bundleTotal.toFixed(0)}
          </button>
          <p className="font-body text-xs text-ink/60">
            {chosen.length}/{bundle.length} matching pieces selected. Sized to the jacket at fitting.
          </p>
        </div>
      </div>
    </div>
  );
}

export default BundleBuilder;
